import {handleRepositoryDataError} from '@github-ui/data-router-error-helpers'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {AnalyticsWrapper as BaseAnalyticsWrapper} from '@github-ui/repo-issues-page-layout/AnalyticsWrapper'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {
  MilestoneEdit as MilestoneEditForm,
  type MilestoneEditFragment,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Reuse the shared edit form during the gated migration.
} from '@github-ui/repository-milestone/MilestoneEdit'
import {useState} from 'react'

import {issuesLayoutRoute} from './issues-layout-route'
import {milestoneEditQueryKey, milestoneEditRoute} from './milestone-edit-route'
import {useRenderSidebar} from './render-issues-sidebar'
import {useMilestoneCacheSync} from './use-milestone-cache-sync'
import {
  useMilestoneEditRequest,
  useMilestoneWritePending,
  useOptimisticMilestoneEdit,
} from './use-optimistic-milestone-edit'

export function MilestoneEdit() {
  const {owner, repo, number: numberParam} = useRouteParams(milestoneEditRoute)
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const payload = useRouteQuery(milestoneEditRoute, 'mainQuery')
  const optimisticPayload = useOptimisticData(payload.data, {
    targets: [{queryKey: milestoneEditQueryKey(owner, repo, Number.parseInt(numberParam, 10))}],
  })
  const syncMilestoneCaches = useMilestoneCacheSync(owner, repo)

  const renderSidebarFn = useRenderSidebar(owner, repo, layoutPayload)
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  // FORBIDDEN/SAML reloads the page; other errors throw to the boundary.
  const data = handleRepositoryDataError(optimisticPayload)
  if (!data?.repository) {
    return null
  }

  // This leaf owns its own layout so the form keeps its milestone chrome in both sidebar-flag states,
  // matching the Relay page it replaces.
  return (
    <BaseAnalyticsWrapper category="Repository Milestone Edit" appName="issues">
      <RepoIssuesPageLayout
        dialogTitle="Milestones"
        title={sidebarLayoutEnabled ? 'Edit milestone' : undefined}
        renderSidebar={renderSidebarFn}
        initialExpanded={layoutPayload?.sidebarExpanded}
      >
        {data.repository.milestone ? (
          <OptimisticMilestoneEditForm
            key={`${owner}/${repo}/${data.repository.milestone.number}`}
            repository={data.repository}
            owner={owner}
            repo={repo}
            number={data.repository.milestone.number}
          />
        ) : (
          <MilestoneEditForm repository={data.repository} onMutationSuccess={syncMilestoneCaches} />
        )}
      </RepoIssuesPageLayout>
    </BaseAnalyticsWrapper>
  )
}

function OptimisticMilestoneEditForm({
  repository,
  owner,
  repo,
  number,
}: {
  repository: MilestoneEditFragment
  owner: string
  repo: string
  number: number
}) {
  const submit = useOptimisticMilestoneEdit(owner, repo, number)
  const request = useMilestoneEditRequest(owner, repo, number)
  const isPending = useMilestoneWritePending(repository.milestone?.id)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const recoverDraft = request?.status === 'error' || request?.status === 'pending'
  return (
    <MilestoneEditForm
      repository={repository}
      onOptimisticSubmit={(input, state) => {
        setHasSubmitted(true)
        submit(input, state)
      }}
      draft={recoverDraft ? request.variables?.input : undefined}
      submissionError={hasSubmitted && request?.status === 'error' ? request.error : undefined}
      isPending={isPending}
    />
  )
}
